import type { FeedbackEvent } from '../sim/feedback';
import { buttonUp, idleInput, type TickInput } from '../sim/input';
import { uiHasFocus } from './ownership';

/**
 * Gamepad → TickInput + right-stick camera + rumble. The Gamepad API is NOT evented — mount polls
 * `poll()` once per render frame and the snapshot diffs feed the same edge-latching contract as
 * KeyboardSource: a press+release observed between ticks still yields exactly one `pressed` tick.
 * (A press+release entirely between two polls is invisible — an API limit, not a latch bug.)
 *
 * Standard mapping only: left stick = move (radial rescaled deadzone + response curve — the
 * documented spec), right stick = camera (expo curve), A/South = jump, B/East = roll. Rumble is a
 * SUBSCRIBER of the feedback bus, exactly like SFX/VFX — no gameplay code knows rumble exists.
 */

const LEFT_DEADZONE = 0.14;
const LEFT_CURVE = 1.5;
const LOOK_DEADZONE = 0.15;
const LOOK_EXPO = 1.8;
const YAW_RATE = Math.PI; // 180°/s at full deflection
const PITCH_RATE = (120 * Math.PI) / 180; // 120°/s

/**
 * RADIAL deadzone with RESCALE (not a hard cut): mag' = ((mag − dz)/(1 − dz))^curve, direction
 * preserved, |out| ≤ 1. Rescaling keeps the just-past-deadzone region usable for slow walks.
 */
export function shapeStick(x: number, y: number, deadzone = LEFT_DEADZONE, curve = LEFT_CURVE): {
  x: number;
  y: number;
} {
  const mag = Math.hypot(x, y);
  if (mag <= deadzone) return { x: 0, y: 0 };
  const scaled = Math.pow(Math.min((mag - deadzone) / (1 - deadzone), 1), curve);
  return { x: (x / mag) * scaled, y: (y / mag) * scaled };
}

/** Per-axis look shaping: rescaled deadzone then sign(v)·|v|^expo — fine aim near center. */
export function shapeLookAxis(v: number, deadzone = LOOK_DEADZONE, expo = LOOK_EXPO): number {
  const a = Math.abs(v);
  if (a <= deadzone) return 0;
  const scaled = Math.min((a - deadzone) / (1 - deadzone), 1);
  return Math.sign(v) * Math.pow(scaled, expo);
}

export class GamepadSource {
  /** Feedback-bus rumble toggle (panel Debug folder mirrors DebugOpts.rumble onto this). */
  enabled = true;

  private pad: Gamepad | null = null;
  private axes: [number, number, number, number] = [0, 0, 0, 0];
  private held = { jump: false, roll: false };
  /**
   * West/X (button 2) held state from the LAST poll. The exploration vertical's interact edge
   * reads this through mount's hooks instead of calling navigator.getGamepads() itself — one
   * getGamepads() per frame instead of two (each call allocates fresh Gamepad snapshots in
   * Chrome), and the vertical stops reaching past its layering into a device API.
   */
  private interactDown = false;
  // Edge latches accumulate across polls and clear on consumeTick (KeyboardSource contract).
  private pressedLatch = { jump: false, roll: false };
  private releasedLatch = { jump: false, roll: false };

  /** Snapshot the first connected pad. Call once per render frame, before the tick loop. */
  poll(): void {
    const pads = typeof navigator !== 'undefined' ? navigator.getGamepads() : [];
    this.pad = null;
    for (const p of pads) {
      if (p?.connected) {
        this.pad = p;
        break;
      }
    }
    if (!this.pad) {
      // Treat disconnect like keyboard blur: release anything held so the sim never sticks.
      if (this.held.jump) this.releasedLatch.jump = true;
      if (this.held.roll) this.releasedLatch.roll = true;
      this.held = { jump: false, roll: false };
      this.axes = [0, 0, 0, 0];
      this.interactDown = false;
      return;
    }
    const a = this.pad.axes;
    this.axes = [a[0] ?? 0, a[1] ?? 0, a[2] ?? 0, a[3] ?? 0];
    const jump = this.pad.buttons[0]?.pressed ?? false; // A / South
    const roll = this.pad.buttons[1]?.pressed ?? false; // B / East
    if (jump && !this.held.jump) this.pressedLatch.jump = true;
    if (!jump && this.held.jump) this.releasedLatch.jump = true;
    if (roll && !this.held.roll) this.pressedLatch.roll = true;
    if (!roll && this.held.roll) this.releasedLatch.roll = true;
    this.held = { jump, roll };
    this.interactDown = this.pad.buttons[2]?.pressed ?? false; // West/X — the interact button
  }

  connected(): boolean {
    return this.pad !== null;
  }

  /** West/X held, from this frame's single poll. The exploration interact edge reads THIS. */
  interactHeld(): boolean {
    return this.interactDown;
  }

  /** Build one tick's input and clear the edge latches. Same contract as KeyboardSource. */
  consumeTick(cameraYaw: number): TickInput {
    // INPUT OWNERSHIP (CLAUDE.md), same law as KeyboardSource: a stage/modal surface owns input ⇒
    // neutral tick, latches drained. The stick can't strafe the character behind the card either.
    if (uiHasFocus()) {
      this.pressedLatch = { jump: false, roll: false };
      this.releasedLatch = { jump: false, roll: false };
      return idleInput();
    }
    // Screen-space intent: x = right, y = forward. Gamepad Y axis is +down, so forward = −axes[1].
    const s = shapeStick(this.axes[0], -this.axes[1]);
    // Rotate into world XZ — identical convention to keyboard.ts (at yaw 0, forward = world −Z).
    const cos = Math.cos(cameraYaw);
    const sin = Math.sin(cameraYaw);
    const input: TickInput = {
      move: { x: s.x * cos - s.y * sin, y: -s.x * sin - s.y * cos },
      jump: {
        held: this.held.jump,
        pressed: this.pressedLatch.jump,
        released: this.releasedLatch.jump,
      },
      roll: {
        held: this.held.roll,
        pressed: this.pressedLatch.roll,
        released: this.releasedLatch.roll,
      },
      // UNBOUND on the pad, deliberately, and this is a decision rather than an omission.
      // Sprint exists because the KEYBOARD is binary: a stick already expresses walk-vs-run
      // through magnitude, so a pad has no equivalent gap to fill. Binding it would also mean
      // choosing a button (L3 is the console convention), and which button a mechanic lives on
      // is a feel decision this repo takes in the hands-on session, not one a source file makes
      // on its own. The merge ORs the two sources, so binding it later is a one-line change here
      // and touches nothing downstream.
      sprint: buttonUp(),
    };
    this.pressedLatch = { jump: false, roll: false };
    this.releasedLatch = { jump: false, roll: false };
    return input;
  }

  /**
   * Right-stick camera deltas for this frame (radians). Signs match the mouse: stick right looks
   * right (yaw decreases), stick down pitches up the same way mouse-down does. `active` lets
   * mount reset rig.msSinceCamInput so auto-yaw assist stands down while the player aims.
   */
  consumeLook(frameDt: number): { dYaw: number; dPitch: number; active: boolean } {
    const lx = shapeLookAxis(this.axes[2]);
    const ly = shapeLookAxis(this.axes[3]);
    return {
      dYaw: -lx * YAW_RATE * frameDt,
      dPitch: ly * PITCH_RATE * frameDt,
      active: lx !== 0 || ly !== 0,
    };
  }

  /** Feedback-bus subscriber. Missing actuator (or a rejected effect) is silently fine. */
  rumble(events: FeedbackEvent[]): void {
    if (!this.enabled || events.length === 0) return;
    const actuator = this.pad?.vibrationActuator;
    if (!actuator) return;
    for (const e of events) {
      let effect: { duration: number; strongMagnitude?: number; weakMagnitude?: number } | null =
        null;
      switch (e.type) {
        case 'land':
          effect = { duration: 90 + 160 * e.intensity, strongMagnitude: 0.25 + 0.75 * e.intensity };
          break;
        case 'roll':
          effect = { duration: 140, strongMagnitude: 0.6 };
          break;
        case 'hoverStart':
          effect = { duration: 80, weakMagnitude: 0.4 };
          break;
        case 'skid':
          effect = { duration: 100, weakMagnitude: 0.5 };
          break;
        case 'break':
          effect = { duration: 120, strongMagnitude: 0.4 };
          break;
        default:
          break;
      }
      if (effect) {
        try {
          void actuator.playEffect('dual-rumble', effect)?.catch?.(() => {});
        } catch {
          // Some browsers throw synchronously on unsupported effect types — never let that bubble.
        }
      }
    }
  }
}
